// infrastructure/mappers/policyholderMapper.js

/**
 * Anti-corruption layer between a person document and this context's demographics projection.
 *
 * The context README's other mappers are ordinary persistence mapping: they translate storage
 * shapes for concepts Revenue Cycle already owns. This one is different, and it is the first of its
 * kind here. A person record belongs to another area of the product, and this translates that
 * area's vocabulary into terms a payer would recognise. Two of those translations are real changes
 * of meaning rather than renames.
 *
 * 1. A recorded "unknown" gender becomes absent. The stored vocabulary carries an explicit unknown
 *    member, and it is a record of not knowing rather than an answer. Carried across as a value it
 *    would satisfy a completeness check and then be rejected by the payer, which is the worst of
 *    both: held nowhere, denied later. Legacy single-letter values are read too, since the
 *    collection holds both those and the spelled-out forms. The field keeps its name: the payer
 *    calls it gender as well, so renaming it here would invent a difference rather than translate
 *    one.
 *
 * 2. `birthDate` becomes a calendar date. The document stores a Timestamp, and this context's own
 *    `toDateStr` REFUSES instants on purpose, because there is no safe convention for reducing one
 *    to a day and picking silently shifts somebody's date by one. It is nonetheless safe here, for
 *    a reason specific to this field: every writer stores a birth date at noon, deliberately, and
 *    both the client editor and the backend service say so in a comment where they do it. Noon
 *    survives a read in any offset within twelve hours either way, which covers every region we
 *    run in. That is a property of this field's writers, not a general licence, so it is done here
 *    rather than by loosening `toDateStr`.
 *
 * 3. The name arrives twice. `clientName` is what the product displays and the parts are what a
 *    payer matches on, and they are not always consistent: the editor only keeps them in step when
 *    they already agreed. Both are carried, and neither is derived from the other.
 *
 * NOTHING IS WRITTEN BACK THROUGH THIS for a person we do not own. Creating or editing one of those
 * goes through a callable that owns the writable field allowlist, and a projection shaped for
 * reading would silently drop every field it does not carry.
 *
 * THE CLIENT IS THE EXCEPTION, and `toClientProfilePatch` is the way back for that one record only.
 * The client's document belongs to the editor this context is embedded in, which holds it as a draft
 * and saves it as a whole. Writing it from here would be a second writer against a document already
 * being edited, so the patch goes back to that draft rather than to the collection. It carries only
 * what this form collects, which is why it is a patch and not a document.
 */

import { viewerTimeZone } from 'src/contexts/identityAndTenancy';
import { atWallClock, readCalendarDate, toCalendarDate } from 'src/kernel/time';

/**
 * The stored vocabulary, in both the spelled-out and legacy single-letter forms.
 *
 * A Map rather than an object, so a stored value of `constructor` or `toString` misses instead of
 * resolving to something off the prototype. A function would be truthy, the fallback below would not
 * fire, and readiness would read a gender we do not have as present.
 */
const GENDER_BY_STORED_VALUE = new Map(Object.entries({
  m: 'male',
  male: 'male',
  f: 'female',
  female: 'female',
  o: 'other',
  other: 'other',
}));

/**
 * Noon-stored instant to the calendar day it was meant to be. Read in the viewer's zone, the one the
 * client editor writes noon in; see the mapper docblock for why the noon convention makes this safe.
 *
 * @param {*} birthDate - a Firestore Timestamp, a Date, or neither
 * @returns {string|null} 'YYYY-MM-DD'
 */
export function toBirthDateStr(birthDate) {
  const date = typeof birthDate?.toDate === 'function' ? birthDate.toDate() : birthDate;

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  return toCalendarDate(date, viewerTimeZone());
} // toBirthDateStr()

/**
 * Trims to null, so an empty string never passes a completeness check.
 *
 * A STRING OR NOTHING, because the demographics say `string|null` and a consumer that believed it
 * would render a number or an array as a name. The collection is schemaless and predates the
 * server-side guard on what may be written, so nothing rules out a stored value of the wrong shape.
 * Null is the honest answer for one: readiness then reports the gap instead of passing it on.
 */
function present(value) {
  return typeof value === 'string' ? (value.trim() || null) : null;
} // present()

/**
 * @param {Object} doc - a person document, with its id
 * @returns {import('../../domain/model/PolicyholderDemographics').PolicyholderDemographics}
 */
export function toPolicyholderDemographics(doc) {
  return {
    personId: doc.id,
    firstName: present(doc.firstName),
    lastName: present(doc.lastName),
    fullName: present(doc.clientName),
    dateOfBirth: toBirthDateStr(doc.birthDate),
    gender: GENDER_BY_STORED_VALUE.get(`${doc.gender ?? ''}`.trim().toLowerCase()) ?? null,
    addressLine1: present(doc.address1),
    city: present(doc.city),
    state: present(doc.state),
    postalCode: present(doc.postalCode),
  };
} // toPolicyholderDemographics()

/** The display name a record would carry if it simply followed its name parts. */
const composedName = (firstName, lastName) => [firstName, lastName]
  .map((part) => `${part ?? ''}`.trim())
  .filter(Boolean)
  .join(' ');

/**
 * Noon on a calendar day in the viewer's zone, the one the client editor writes it in.
 *
 * Never `new Date('1985-09-02')`, which reads a date-only string as UTC midnight, so anywhere behind
 * UTC the day lands on the one before — the exact shift the noon convention exists to prevent. A
 * well-shaped date that cannot exist, like 2026-02-30, is refused rather than rolled into March and
 * stored as a real birthday.
 *
 * @param {string|null} calendarDate - 'YYYY-MM-DD'
 * @returns {Date|null}
 */
function noonOn(calendarDate) {
  // A STRING, not something that stringifies into one: `${['1985-09-02']}` is '1985-09-02', so an
  // array of one date would pass the pattern and be written as a real birthday.
  if (typeof calendarDate !== 'string' || readCalendarDate(calendarDate) === null) {
    return null;
  }

  return atWallClock(calendarDate, { hour: 12 }, viewerTimeZone());
} // noonOn()

/**
 * The way back for the client's own record: this form's fields in client-document vocabulary.
 *
 * THE DISPLAY NAME IS A PREFERRED NAME, not a composition, and this form has no field for it. So it
 * is only rewritten when doing so cannot lose anything: when there is none, or when it still matches
 * what the parts on file compose to and is therefore following them. Anything else is a name somebody
 * chose — a Bobby who is a Robert on the card — and correcting a date of birth is not the moment to
 * throw it away, silently, from a form that never showed it. Same rule the client's own editor
 * applies when its name fields change, and the reason the projection carries both without deriving
 * either.
 *
 * DELIBERATELY STRICTER THAN THE ONE FOR EVERYBODY ELSE, which also composes when the record has no
 * parts at all. On a non-client that means a legacy full name nothing was ever split from, so
 * following the parts is the correction surfacing. Here it means a Bobby with no Robert on file yet,
 * and following the parts would rename them everywhere the product shows a name. The two rules read
 * alike and mean opposite things, because the field does.
 *
 * The birth date comes back as a plain `Date` at noon. Which date type the store wants is the
 * host's business, and naming it here would put a storage dependency inside this context.
 *
 * @param {Object} profile - as collected by the policyholder form
 * @param {import('../../domain/model/PolicyholderDemographics').PolicyholderDemographics|null}
 *   [onFile] - the record as it stands, which decides whether the display name may be rewritten
 * @returns {Object} fields to merge into the client draft; `clientName` only when it may be rewritten
 */
export function toClientProfilePatch(profile, onFile = null) {
  const firstName = `${profile.firstName ?? ''}`.trim();
  const lastName = `${profile.lastName ?? ''}`.trim();

  const patch = {
    firstName,
    lastName,
    birthDate: noonOn(profile.birthDate),
    gender: profile.gender ?? null,
    address1: profile.address1 ?? '',
    city: profile.city ?? '',
    state: profile.state ?? '',
    postalCode: profile.postalCode ?? '',
  };

  const displayed = `${onFile?.fullName ?? ''}`.trim();
  const followsTheParts = displayed === composedName(onFile?.firstName, onFile?.lastName);

  if (displayed === '' || followsTheParts) {
    patch.clientName = composedName(firstName, lastName);
  }

  return patch;
} // toClientProfilePatch()
